import React from 'react';
import { CDSLFetchMode } from './CDSLFetchMode';
import { FetchMode } from './FetchMode';
import { Tabs, ScreenHeader } from './ui';
import { IconBolt, IconGeneratorMailback } from './icons';

/**
 * "Fetch it for me" path. Surfaces both supported fetch flows side-by-side
 * behind a tab selector:
 *
 *  - CDSL OTP fetch (instant demat CAS via OTP)
 *  - KFintech/CAMS mailback (MF CAS emailed to the investor)
 *
 * User picks the source that matches their portfolio. When only one is
 * enabled, the tab header is hidden.
 */

export type FetchTab = 'CDSL' | 'KFINTECH';

interface FetchHubProps {
  enableCdslFetch: boolean;
  enableGenerator: boolean;
  defaultTab?: FetchTab;
  prefill?: { pan?: string; email?: string; boId?: string; dob?: string };
  onCdslRequestOtp: React.ComponentProps<typeof CDSLFetchMode>['onRequestOtp'];
  onCdslVerifyOtp: React.ComponentProps<typeof CDSLFetchMode>['onVerifyOtp'];
  onGeneratorSubmit: React.ComponentProps<typeof FetchMode>['onSubmit'];
  /** Called when the mailback "request sent" confirmation is dismissed. */
  onGeneratorContinueToUpload: () => void;
  /**
   * Optional handoff to the inbound-email flow from the mailback
   * confirmation screen. When wired (i.e. inbound email is enabled), the
   * confirmation screen offers it as the primary CTA.
   */
  onGeneratorJumpToInbound?: (email: string) => void;
  onBack: () => void;
  generatorLoading: boolean;
  /** Password the mailed CAS will be encrypted with; surfaced on the
   * generator success screen. See `FetchMode.casPassword`. */
  generatorCasPassword?: string;
  onEvent?: (event: string, metadata?: any) => void;
}

export const FetchHub: React.FC<FetchHubProps> = ({
  enableCdslFetch,
  enableGenerator,
  defaultTab,
  prefill,
  onCdslRequestOtp,
  onCdslVerifyOtp,
  onGeneratorSubmit,
  onGeneratorContinueToUpload,
  onGeneratorJumpToInbound,
  onBack,
  generatorLoading,
  generatorCasPassword,
  onEvent,
}) => {
  const singleMode: FetchTab | null = enableCdslFetch && !enableGenerator
    ? 'CDSL'
    : !enableCdslFetch && enableGenerator
    ? 'KFINTECH'
    : null;

  const [tab, setTab] = React.useState<FetchTab>(
    singleMode || defaultTab || (enableCdslFetch ? 'CDSL' : 'KFINTECH'),
  );
  const showTabs = !singleMode;

  return (
    <div>
      {showTabs && (
        <>
          <ScreenHeader
            icon={<IconBolt size={22} />}
            title="Fetch your statement"
            description="We'll fetch it directly — your full demat (CDSL + NSDL) via OTP, or your mutual fund CAS by email. No upload needed."
          />
          <Tabs
            tabs={[
              { id: 'CDSL', label: 'Stocks (via CDSL)', icon: <IconBolt size={14} /> },
              { id: 'KFINTECH', label: 'Mutual Funds', icon: <IconGeneratorMailback size={14} /> },
            ]}
            activeId={tab}
            onChange={(id) => {
              onEvent?.('MODE_SWITCHED', { from: tab, to: id });
              setTab(id as FetchTab);
            }}
          />
        </>
      )}

      {tab === 'CDSL' && enableCdslFetch && (
        <CDSLFetchMode
          prefill={prefill}
          onRequestOtp={onCdslRequestOtp}
          onVerifyOtp={onCdslVerifyOtp}
          onBack={onBack}
        />
      )}
      {tab === 'KFINTECH' && enableGenerator && (
        <FetchMode
          prefill={prefill}
          onSubmit={onGeneratorSubmit}
          onContinueToUpload={onGeneratorContinueToUpload}
          onJumpToInbound={onGeneratorJumpToInbound}
          onBack={onBack}
          isLoading={generatorLoading}
          casPassword={generatorCasPassword}
        />
      )}
    </div>
  );
};
